import CircledBackButton from "@/features/Common/components/CircledBackButton";
import DependantsDropdown from "@/features/Common/components/DependantsDropdown";
import IconLoader from "@/features/Common/components/IconLoader";
import { trackPageViewV2 } from "@/services/analytics-adapter";
import {
  findMainMemberInMembership,
  MembershipDependent,
  MembershipDependentIdentityDocumentType,
} from "@/services/core-api-adapter";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import { useGlobalStore } from "@/store";
import { convertPxToRem } from "@/utils";
import { PassportCountryCode } from "@/utils/countries";
import { isValid as isValidInternationalPassportNumber } from "@/utils/validators/InternationalPassportNumber/InternationalPassportNumber";
import { isValid as isValidZAIDNumber } from "@/utils/validators/ZAIDNumber/ZAIDNumber";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useGetMemberships from "../../hooks/useGetMemberships";
import ButtonWithAnalytics from "../ButtonWithAnalytics";
import DefaultDialog from "../DefaultDialog";
import IdentificationNumberToggle from "../IdentificationNumberToggle";
import PassportCountryOfIssueCode from "../PassportCountryOfIssueCode";

interface MedicalAssistanceDependantSelectionProps {
  onNextButtonClick: (data?: any) => void;
  dependants: MembershipDependent[];
}

export default function WhyAreWeAskingYouThisModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <DefaultDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t("WhyAreWeAskingYouThisModal.title")}
      actionButtons={
        <Button size="small" onClick={() => onClose()}>
          {t("common.close")}
        </Button>
      }
    >
      <Stack textAlign="center" spacing={1}>
        {(
          t<any, any, string[]>(
            "MedicalAssistanceDependentSelection.modal.body",
            {
              returnObjects: true,
            }
          ) as []
        ).map((text: string, index: number) => (
          <Typography key={String(text + index)}>{text}</Typography>
        ))}
      </Stack>
    </DefaultDialog>
  );
}

export function MedicalAssistanceDependantSelection({
  onNextButtonClick,
  dependants,
}: MedicalAssistanceDependantSelectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const { state } = useGlobalStore();
  const [dependentsToShow, setDependentsToShow] =
    useState<MembershipDependent[]>(dependants);
  const [selectedMember, setSelectedMember] =
    useState<MembershipDependent | null>(null);
  const [identificationType, setIdentificationType] = useState("");
  const [identificationValue, setIdentificationValue] = useState("");
  const [passportCountryCode, setPassportCountryCode] =
    useState<PassportCountryCode | null>(null);

  const [shouldShowMembersDropdown, setShouldShowMembersDropdown] = useState<
    boolean | null
  >(null);

  const { firstMembership, isMembershipsLoading, membershipsFetchError } =
    useGetMemberships();

  useEffect(() => {
    const mainMember = findMainMemberInMembership(
      firstMembership,
      state.currentUser.memberId
    );
    const updatedDependants = dependentsToShow;
    const mainMemberToAdd = {
      memberId: state.currentUser.memberId,
      firstName: state.currentUser.firstName,
      lastName: state.currentUser.lastName,
      identityDocumentType: mainMember?.memberDetails
        .identityDocumentType as unknown as MembershipDependentIdentityDocumentType,
      identityDocumentValue: mainMember?.memberDetails.identityDocumentValue,
      sexAtBirth: "",
      dateOfBirth: mainMember?.memberDetails.dateOfBirth,
      identityDocumentCountryOfIssue: "",
      status: "active",
    };

    if (
      updatedDependants.find(
        (member) => member.memberId === mainMemberToAdd.memberId
      ) === undefined
    ) {
      updatedDependants.push(mainMemberToAdd);
    }

    setDependentsToShow(updatedDependants);
  }, [isMembershipsLoading, membershipsFetchError]);

  function onBackButtonClick() {
    navigate("/home");
  }

  const [isNextButtonEnabled, setIsNextButtonEnabled] = useState(false);

  function onHandleNextButtonClick(member: MembershipDependent) {
    let updatedMember: MembershipDependent = member;

    if (identificationValue && identificationType) {
      updatedMember = {
        ...member,
        identityDocumentType:
          identificationType === "PassportNumber"
            ? MembershipDependentIdentityDocumentType.PASSPORT_NUMBER
            : MembershipDependentIdentityDocumentType.ID_NUMBER,
        identityDocumentValue: identificationValue,
      };

      if (
        identificationType === "PassportNumber" &&
        passportCountryCode != null
      ) {
        updatedMember = {
          ...member,
          identityDocumentType:
            MembershipDependentIdentityDocumentType.PASSPORT_NUMBER,
          identityDocumentValue: identificationValue,
          identityDocumentCountryOfIssue: passportCountryCode.alpha2,
        };
      }
    }
    onNextButtonClick(updatedMember);
  }

  function handleCountryCodeChange(
    selectedCountry: PassportCountryCode | null
  ) {
    setPassportCountryCode(selectedCountry);
  }

  function handleSelectedMember(selectedMemberMembershipID: any) {
    const getSelectedMember = dependants.find(
      (member: MembershipDependent) =>
        member.memberId === selectedMemberMembershipID
    );
    setSelectedMember(getSelectedMember as any);
    setIsNextButtonEnabled(true);
  }

  const handleIdentificationToggleChange = (
    type: SetStateAction<string>,
    value: SetStateAction<string>
  ) => {
    setIdentificationType(type);
    setIdentificationValue(value);
  };

  function isValid(_identificationType: string, _identificationValue: string) {
    switch (_identificationType) {
      case "IdNumber":
        return isValidZAIDNumber(_identificationValue);
      case "PassportNumber":
        return isValidInternationalPassportNumber(_identificationValue);
    }
    return false;
  }

  function updateNextButtonDisabledState() {
    if (
      identificationType === "PassportNumber" &&
      passportCountryCode === null
    ) {
      setIsNextButtonEnabled(false);
    } else {
      setIsNextButtonEnabled(isValid(identificationType, identificationValue));
    }
  }

  function onModalOpen() {
    setIsModalOpen(true);
  }

  function onModalClose() {
    setIsModalOpen(false);
  }

  useEffect(() => {
    updateNextButtonDisabledState();
  }, [identificationType, identificationValue, passportCountryCode]);

  useEffect(() => {
    const memberHasDependants = dependants.length > 0;
    if (!memberHasDependants) {
      onNextButtonClick();
    } else {
      setShouldShowMembersDropdown(memberHasDependants);
    }
  }, [dependants]);

  useEffect(() => {
    trackPageViewV2({
      pageName: "Medical assistance:Select dependent",
      pageSubSection1: "Medical assistance",
      pageSubSection2: "Medical assistance:Select dependent",
      pageCategory: "Medical assistance",
    });
  }, []);

  return (
    <>
      <Stack
        color="neutral.dark"
        sx={{
          minHeight: "100%",
          justifyContent: "space-between",
          flex: 1,
        }}
      >
        <Box pb={2}>
          <Stack>
            <CircledBackButton
              showLabel={true}
              label={t("common.backButton")}
              onClick={onBackButtonClick}
            />
          </Stack>
          <Stack>
            <IconLoader
              icon="ChatHeartIcon"
              color="error"
              sx={{
                fontSize: convertPxToRem(56),
                alignSelf: "center",
              }}
            />
            <Stack textAlign="center" spacing={2} py={4}>
              <Typography variant="h1" color="inherit">
                {t("MedicalAssistanceDependentSelection.title")}
              </Typography>

              <Typography variant="h3" color="inherit" px={2}>
                {t(`MedicalAssistanceDependentSelection.subTitle`)}
              </Typography>
            </Stack>
            <Typography variant="body2">
              {t("MedicalAssistanceDependentSelection.selectMemberLabel")}
            </Typography>

            {shouldShowMembersDropdown && (
              <DependantsDropdown
                setSelectedMembershipId={handleSelectedMember}
                dependants={dependentsToShow}
                selectMemberLabel=""
                selectMemberPlaceholder={t(
                  "MedicalAssistanceDependentSelection.selectMemberPlaceholder"
                )}
              />
            )}

            {isFeatureEnabled(
              FeatureToggleIdentifier.ENABLE_DEPENDANT_SERVICE_OFFERING
            ) &&
              ((selectedMember !== null &&
                selectedMember?.identityDocumentType === null) ||
                selectedMember?.identityDocumentValue === null) && (
                <Stack mt={3} spacing={2}>
                  <Divider />
                  <Typography variant="body1" fontWeight="bold">
                    {t(
                      "MedicalAssistanceDependentSelection.collectAdditionalDetails.title"
                    )}
                  </Typography>
                  <ButtonWithAnalytics
                    page="MedicalAssistanceDependantSelection"
                    variant="text"
                    intent="navigational"
                    text={t("WhyAreWeAskingYouThisModal.title")}
                    sx={{ alignSelf: "flex-start", padding: 0 }}
                    onClick={onModalOpen}
                    size="small"
                  >
                    {t("WhyAreWeAskingYouThisModal.title")}
                  </ButtonWithAnalytics>
                  <IdentificationNumberToggle
                    existingValue={{
                      identificationType: "",
                      identificationValue: "",
                    }}
                    onIdentificationToggleChange={
                      handleIdentificationToggleChange
                    }
                  />
                  {identificationType === "PassportNumber" && (
                    <PassportCountryOfIssueCode
                      showLabel={true}
                      label={t("common.passportCountryCode")}
                      onCountryCodeChange={handleCountryCodeChange}
                    />
                  )}
                </Stack>
              )}
          </Stack>
        </Box>

        <ButtonWithAnalytics
          page="Medical assistance dependant selection"
          text={t(`common.nextButton`)}
          intent="navigational"
          color={"primary"}
          variant="contained"
          onClick={() => onHandleNextButtonClick(selectedMember as any)}
          fullWidth
          disabled={!isNextButtonEnabled}
        >
          {t(`common.nextButton`)}
        </ButtonWithAnalytics>
      </Stack>

      <WhyAreWeAskingYouThisModal isOpen={isModalOpen} onClose={onModalClose} />
    </>
  );
}
